import React from 'react';
import { createPortal } from 'react-dom';

/* ---------------------------------------------------------------------------
   ScopedPortalPopup

   Renders a popup into document.body with fully self-contained inline
   positioning, so no global .modal / .modal-overlay rule from another page's
   CSS can move or hide it.

   `scopeClass` is put on the popup box itself (e.g. "time-off-page to-popup").
   That lets the page's own scoped CSS (".time-off-page input", etc.) keep
   styling the controls inside the popup even though it now lives outside the
   page's DOM tree. The inline styles below override the page-root padding /
   background / min-height that the scope class would otherwise bring along.

   level 0 = main popup (999998 / 999999)
   level 1 = popup opened on top of another popup (1999998 / 1999999)
   level 2 = warning on top of that (2999998 / 2999999)
--------------------------------------------------------------------------- */
const Z_LEVELS = [
  [999998, 999999],
  [1999998, 1999999],
  [2999998, 2999999],
];

const ScopedPortalPopup = ({
  onClose,
  scopeClass = '',
  width = 660,
  level = 0,
  role = 'dialog',
  children,
}) => {
  if (typeof document === 'undefined') return null;
  const [zOverlay, zBox] = Z_LEVELS[level] || Z_LEVELS[0];

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,.55)',
          zIndex: zOverlay,
        }}
      />
      <div
        className={scopeClass}
        role={role}
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: zBox,
          width: `min(${width}px, calc(100vw - 32px))`,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '88vh',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: 0,
          padding: '20px 22px',
          boxSizing: 'border-box',
          background: '#ffffff',
          border: '1px solid #dbe2ea',
          borderRadius: 12,
          boxShadow: '0 18px 48px rgba(15, 23, 42, 0.24)',
          textAlign: 'left',
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

export default ScopedPortalPopup;
